import type { AdminVendorRow } from "@/features/admin/marketplace/types/admin-marketplace.types";
import type { SortValue } from "@/features/admin/lib/table-sort";
import { matchesQuery } from "@/features/llm-usage/lib/table-filters";

export type VendorLinkedFilter = "all" | "linked" | "unlinked";
export type VendorStatusFilter = "all" | "active" | "inactive";

export type VendorSortKey =
  | "name"
  | "contactEmail"
  | "catalog"
  | "created"
  | "updated"
  | "status"
  | "linked"
  | "connected";

export interface VendorListFilters {
  search: string;
  linked: VendorLinkedFilter;
  status: VendorStatusFilter;
}

type VendorContactEmailSource = Pick<AdminVendorRow, "contactEmail" | "contactEmails">;

export function isVendorLinked(vendor: AdminVendorRow): boolean {
  return Boolean(vendor.partnerTenantId ?? vendor.partnerTenant?.id);
}

/**
 * Address shown in the admin Partners list.
 *
 * Pending addresses must not look live: an approved primary wins, otherwise
 * the first approved row. The legacy column is only for vendors with no
 * per-address rows — the same choice the form makes, but list rows are keyed
 * by `id` / `isPrimary` rather than the form's `uid`.
 */
export function getVendorDisplayContactEmail(vendor: VendorContactEmailSource): string | null {
  if (vendor.contactEmails.length === 0) {
    return vendor.contactEmail;
  }

  const approved = vendor.contactEmails.filter((row) => row.approval === "APPROVED");
  const chosen = approved.find((row) => row.isPrimary) ?? approved[0];
  return chosen?.email ?? null;
}

export function vendorMatchesQuery(vendor: AdminVendorRow, query: string): boolean {
  const haystack = [vendor.name, vendor.slug, ...contactEmailsForSearch(vendor)]
    .filter(Boolean)
    .join(" ");
  return matchesQuery(haystack, query);
}

export function applyVendorListFilters(
  vendors: AdminVendorRow[],
  filters: VendorListFilters,
): AdminVendorRow[] {
  return vendors.filter((vendor) => {
    if (!vendorMatchesQuery(vendor, filters.search)) return false;

    if (filters.linked === "linked" && !isVendorLinked(vendor)) return false;
    if (filters.linked === "unlinked" && isVendorLinked(vendor)) return false;

    if (filters.status === "active" && !vendor.isActive) return false;
    if (filters.status === "inactive" && vendor.isActive) return false;

    return true;
  });
}

/**
 * Catalog sorts by package count first, then question count packed into one
 * number so a tie on packages still ranks by questions without a second pass.
 */
export function vendorSortValue(vendor: AdminVendorRow, key: VendorSortKey): SortValue {
  switch (key) {
    case "name":
      return vendor.name;
    case "contactEmail":
      return getVendorDisplayContactEmail(vendor);
    case "catalog":
      return vendor.packages.length * 1_000_000 + vendor.questions.length;
    case "created":
      return Date.parse(vendor.createdAt);
    case "updated":
      return Date.parse(vendor.updatedAt);
    case "status":
      return vendor.isActive ? 1 : 0;
    case "linked":
      return isVendorLinked(vendor) ? 1 : 0;
    case "connected":
      return vendor.connectedCount ?? 0;
  }
}

export function isVendorListFiltered(filters: VendorListFilters): boolean {
  return (
    filters.search.trim() !== "" ||
    filters.linked !== "all" ||
    filters.status !== "all"
  );
}

function contactEmailsForSearch(vendor: VendorContactEmailSource): string[] {
  if (vendor.contactEmails.length === 0) {
    return vendor.contactEmail ? [vendor.contactEmail] : [];
  }

  return vendor.contactEmails
    .filter((row) => row.approval === "APPROVED")
    .map((row) => row.email);
}
